import React from 'react';
import { Contact, Entity } from '@admiin-com/ds-graphql';
import {
  WBBox,
  WBFlex,
  WBLink,
  WBTextField,
  WBTypography,
} from '@admiin-com/ds-web';
import {
  Autocomplete,
  CircularProgress,
  InputBase,
  MenuItem,
  Paper,
} from '@mui/material';
import { debounce } from '@mui/material/utils';
import { useTranslation } from 'react-i18next';
import { v4 as uuidv4 } from 'uuid';
import { useLookupService } from '../AutoCompleteLookup/useLookupService';
import { TextFieldProps } from 'libs/design-system-web/src/lib/components/composites/TextField/TextField';

export type ServicesLookupProps = TextFieldProps & {
  value: any;
  entityId: string;
  isFormField?: boolean;
  onChange: (value: any) => void;
  onCreateNew: () => void;
  placeholder?: string;
};

const ServicesLookup = (
  props: ServicesLookupProps,
  ref: React.ForwardedRef<HTMLDivElement>
) => {
  const { value, entityId, onChange } = props;
  const { t } = useTranslation();

  const [inputValue, setInputValue] = React.useState(value);

  const [options, setOptions] = React.useState<readonly any[]>([]);

  const { lookup, loading } = useLookupService({ type: 'Service', entityId });

  console.log(inputValue, value);

  //TODO: refactor code a bit?
  const fetch = React.useMemo(() => {
    return debounce(async (inputValue) => {
      if (!lookup) return;
      const results = await lookup(inputValue);
      setOptions([
        ...(results && Array.isArray(results)
          ? results?.filter((result) => result) ?? []
          : []),
      ]);
    }, 150);
  }, [lookup]);

  const [open, setOpen] = React.useState(props.isFormField ? false : true);
  const initalLoading = !value && open;

  React.useEffect(() => {
    let active = true;

    if (!initalLoading) {
      return undefined;
    }

    (async () => {
      if (active) {
        fetch('');
      }
    })();

    return () => {
      active = false;
    };
  }, [fetch, initalLoading, entityId]);

  React.useEffect(() => {
    // if (inputValue === value?.label || (value && value?.label === undefined)) {
    // fetch('');
    // } else
    fetch(inputValue);
  }, [inputValue]);

  const handleNewClick = (e: any) => {
    // e.stopPropagation();
    // e.preventDefault();
    props.onCreateNew();
  };
  // React.useEffect(() => {
  //   if (!open) {
  //     setOptions([]);
  //   }
  // }, [open]);

  React.useEffect(() => {
    if (value)
      setInputValue(
        typeof value === 'string' ? value : value?.description ?? ''
      );
  }, [value]);

  const addNewBox = React.useMemo(
    () => (
      <WBBox p={2}>
        <WBLink
          variant="body2"
          component={'button'}
          // sx={{ marginTop: 3 }}
          underline="always"
          color={'text.primary'}
          fontWeight={'bold'}
          onClick={handleNewClick}
        >
          {`${t('addNew', { ns: 'taskbox' })} ${t('service', {
            ns: 'taskbox',
          })}`}
        </WBLink>
      </WBBox>
    ),
    [t]
  );
  // console.log("value**", value, !inputValue ? "noinput" : "input", options);
  const changedRef = React.useRef(false);

  return (
    <WBBox sx={{ width: '100%' }}>
      <Autocomplete
        fullWidth
        open={open}
        onOpen={() => {
          setOpen(true);
        }}
        onClose={() => {
          setOpen(false);
        }}
        getOptionLabel={(option) => {
          // console.log(option, "option", !option?.description ? "no description" : "description");
          return option?.description ?? '';
        }}
        filterOptions={(x) => x}
        options={options}
        autoComplete
        blurOnSelect={true}
        inputValue={inputValue}
        includeInputInList
        filterSelectedOptions
        value={value ?? null}
        noOptionsText={
          <WBBox mx={-2}>
            <WBTypography color={'inherit'} mb={1} ml={2}>
              {t(`no${'description'}`, { ns: 'taskbox' })}
            </WBTypography>
            {addNewBox}
          </WBBox>
        }
        forcePopupIcon={true}
        // inputValue={inputValue}
        onChange={(event: React.ChangeEvent<object>, newValue: any) => {
          onChange(newValue);
          changedRef.current = true;
          console.log('newValue', newValue);
        }}
        onInputChange={(event, newInputValue) => {
          // console.log("newInputValue", newInputValue);
          setInputValue(newInputValue);
          // onChange({ description: newInputValue });
        }}
        onFocus={(event) => {
          changedRef.current = false;
        }}
        onBlur={(event) => {
          if (!changedRef.current)
            onChange({ description: inputValue, id: null });
        }}
        onKeyDown={(event) => {
          if (event.key === 'Tab') {
            if (!changedRef.current) {
              onChange({ description: inputValue, id: null });
            }
            // Let the default Tab behavior continue
            return;
          }
        }}
        // clearIcon={<WBIcon name="Close" color={'black'} size={'small'} />}
        loading={loading}
        freeSolo
        disableClearable
        loadingText={t('searchingService', { ns: 'services' })}
        renderInput={(params) => {
          return !props.isFormField ? (
            <InputBase
              inputRef={ref}
              {...params.InputProps}
              value={params.inputProps.value}
              inputProps={{
                ...params.inputProps,
                placeholder: props.placeholder,
              }}
              fullWidth
              sx={{ paddingX: 1, fontSize: '14px' }}
              endAdornment={
                <WBFlex alignItems={'center'}>
                  {loading ? (
                    <CircularProgress color="inherit" size={18} />
                  ) : null}
                  {params.InputProps.endAdornment}
                </WBFlex>
              }
              placeholder={props.placeholder}
              autoFocus
            />
          ) : (
            <WBTextField
              label={t('description', { ns: 'taskbox' })}
              ref={ref}
              {...params}
              placeholder={props.placeholder || 'Select a service'}
              fullWidth
              InputProps={{
                ...params.InputProps,
                endAdornment: (
                  <React.Fragment>
                    {loading ? (
                      <CircularProgress color="inherit" size={20} />
                    ) : null}
                    {params.InputProps.endAdornment}
                  </React.Fragment>
                ),
              }}
              helperText={props.helperText}
              error={props.error}
            />
          );
        }}
        PaperComponent={({ children, ...props }) => (
          <Paper
            {...props}
            sx={{
              px: 0,
              boxShadow: '0px 5px 15px rgba(0, 0, 0, 0.1)',
              elevation: 3,
            }}
          >
            {React.Children.map(children, (child) => child)}
          </Paper>
        )}
        renderOption={(props, option, state, ownerState) => {
          return (
            <React.Fragment key={uuidv4()}>
              <MenuItem {...props} value={option?.value} divider>
                {option.description}
              </MenuItem>
              {state.index + 1 === ownerState.options.length ? (
                <WBBox>{addNewBox}</WBBox>
              ) : null}
            </React.Fragment>
          );
        }}
        autoFocus
      />
    </WBBox>
  );
};

export default React.forwardRef(ServicesLookup) as <
  T extends Entity | Contact | null
>(
  props: ServicesLookupProps & React.RefAttributes<HTMLDivElement>
) => React.ReactElement;
